import { useState } from 'react';
import { Search, Package, Truck, CheckCircle, Clock, XCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import OrderTimeline from '../components/OrderTimeline';

const API = `${window.location.origin}/api`;

const STATUS_CONFIG = {
  'Pending Payment': { icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/20', step: 1 },
  'Paid': { icon: CheckCircle, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20', step: 2 },
  'Preparing': { icon: Package, color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20', step: 3 },
  'Shipped': { icon: Truck, color: 'text-cyan-400', bg: 'bg-cyan-500/10 border-cyan-500/20', step: 4 },
  'Completed': { icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-500/10 border-green-500/20', step: 5 },
  'Rejected': { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20', step: -1 },
};

const STEPS = ['Pending Payment', 'Paid', 'Preparing', 'Shipped', 'Completed'];

export default function OrderLookupPage() {
  const [orderNumber, setOrderNumber] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    const trimmed = orderNumber.trim();
    if (!trimmed) return;
    setLoading(true);
    setError('');
    setOrder(null);
    try {
      const res = await axios.get(`${API}/orders/${encodeURIComponent(trimmed)}`);
      setOrder(res.data);
    } catch (err) {
      setError(err.response?.status === 404 ? 'Order not found. Please check your order number.' : 'Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  const statusConfig = order ? STATUS_CONFIG[order.status] || STATUS_CONFIG['Pending Payment'] : null;
  const StatusIcon = statusConfig?.icon || Clock;
  const currentStep = statusConfig?.step || 0;

  return (
    <div data-testid="order-lookup-page" className="min-h-screen pb-16">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <h1 className="text-3xl sm:text-4xl tracking-tight font-medium text-white mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Track Your Order
        </h1>
        <p className="text-white/40 text-sm font-sans mb-8">Enter your order number to check the status of your order.</p>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="flex gap-2 mb-8" data-testid="order-search-form">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input
              type="text"
              value={orderNumber}
              onChange={e => setOrderNumber(e.target.value)}
              placeholder="e.g. JSF-260402-A1B2C3"
              className="w-full pl-10 pr-4 py-3 bg-transparent border border-white/20 text-white text-sm focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-white/30"
              data-testid="order-number-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading || !orderNumber.trim()}
            className="bg-[#D4AF37] text-black px-6 py-3 text-sm uppercase tracking-[0.1em] font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-50 whitespace-nowrap"
            data-testid="track-order-btn"
          >
            {loading ? 'Searching...' : 'Track'}
          </button>
        </form>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/20 p-4 mb-6" data-testid="order-lookup-error">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-red-400 text-sm font-sans">{error}</p>
          </div>
        )}

        {/* Order Result */}
        {order && (
          <div className="space-y-6 animate-in fade-in duration-300" data-testid="order-result">
            {/* Status Header */}
            <div className={`border p-5 ${statusConfig.bg}`}>
              <div className="flex items-center gap-3">
                <StatusIcon className={`w-6 h-6 ${statusConfig.color}`} />
                <div>
                  <p className="text-white text-sm font-sans font-medium">{order.status}</p>
                  <p className="text-white/40 text-xs font-sans font-mono">{order.order_number}</p>
                </div>
              </div>
            </div>

            {/* New visual timeline */}
            <OrderTimeline status={order.status} statusHistory={order.status_history || []} />

            {/* Tracking Number */}
            {order.tracking_number && (
              <div className="bg-[#141414] border border-white/5 p-4" data-testid="tracking-info">
                <p className="text-xs uppercase tracking-wider text-white/40 mb-1 font-sans">Tracking Number</p>
                <p className="text-white text-sm font-mono">{order.tracking_number}</p>
              </div>
            )}

            {/* Order Details */}
            <div className="bg-[#141414] border border-white/5 p-4 sm:p-5" data-testid="order-details">
              <p className="text-xs uppercase tracking-wider text-[#D4AF37] mb-3 font-sans font-medium">Order Details</p>
              {order.items?.map((item, i) => (
                <div key={i} className="flex justify-between items-center py-2 border-b border-white/5 last:border-0">
                  <div>
                    <p className="text-white text-sm font-light">{item.product_name} <span className="text-white/40">x{item.quantity}</span></p>
                    {item.variant && <p className="text-white/30 text-xs">{item.variant.size} &middot; {item.variant.type}</p>}
                  </div>
                  <p className="text-white/60 text-sm font-sans">P{(item.price * item.quantity).toLocaleString()}</p>
                </div>
              ))}
              <div className="pt-3 mt-2 border-t border-white/10 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-white/50 font-sans">Subtotal</span>
                  <span className="text-white/60 font-sans">P{order.subtotal?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/50 font-sans">Shipping</span>
                  <span className="text-white/60 font-sans">{order.shipping_fee === 0 ? 'Free' : order.shipping_fee ? `P${order.shipping_fee?.toLocaleString()}` : 'N/A'}</span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t border-white/10">
                  <span className="text-white font-sans font-medium uppercase tracking-wider">Total</span>
                  <span className="text-[#D4AF37] font-sans font-medium">P{order.total?.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Delivery Info */}
            <div className="bg-[#141414] border border-white/5 p-4 sm:p-5" data-testid="delivery-info">
              <p className="text-xs uppercase tracking-wider text-[#D4AF37] mb-3 font-sans font-medium">Delivery Information</p>
              <div className="space-y-1 text-sm">
                <p className="text-white">{order.customer_name}</p>
                <p className="text-white/50">{order.phone}</p>
                {order.address && (
                  <p className="text-white/50">
                    {order.address.house}, {order.address.barangay}, {order.address.city}, {order.address.province} {order.address.zip}
                  </p>
                )}
              </div>
            </div>

            {/* Order Date */}
            <p className="text-white/20 text-xs font-sans text-center">
              Order placed: {new Date(order.created_at).toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
        )}

        {/* Help Text */}
        {!order && !error && !loading && (
          <div className="text-center py-12" data-testid="order-lookup-help">
            <Package className="w-10 h-10 text-white/10 mx-auto mb-3" />
            <p className="text-white/30 text-sm font-sans">Your order number was provided in your order confirmation.</p>
            <p className="text-white/20 text-xs font-sans mt-1">It looks like JSF-YYMMDD-XXXXXX</p>
          </div>
        )}
      </div>
    </div>
  );
}
